import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { leadsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  PlusCircle,
  Search,
  Filter,
  ExternalLink,
  Loader2,
  Users,
  Zap,
  Building2,
  Home,
  Heart,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';

const MILESTONES = [
  'Lead Captured',
  'Qualified',
  'Proposal Sent',
  'Contract Signed',
  'Site Survey Completed',
  'Installation Scheduled',
  'Installation Completed',
  'PTO / Project Complete',
  'Sale Completed',
  'Rep Paid Out',
];

const Leads = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const loadLeads = useCallback(async () => {
    try {
      const response = await leadsAPI.getAll();
      setLeads(response.data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;
    const matchesType = typeFilter === 'all' || lead.lead_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getMilestoneColor = (status) => {
    const index = MILESTONES.indexOf(status);
    if (index === -1) return 'bg-slate-100 text-slate-700';
    if (index === MILESTONES.length - 1) return 'bg-emerald-600 text-white';
    if (index >= 6) return 'bg-emerald-100 text-emerald-700';
    if (index >= 4) return 'bg-amber-100 text-amber-700';
    if (index >= 2) return 'bg-blue-100 text-blue-700';
    return 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leads-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 dark:text-white">
            Leads
          </h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Manage your solar leads and track their progress
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(user?.role === 'Admin' || user?.role === 'BackOffice') && (
            <Link to="/leads/import">
              <Button variant="outline" data-testid="import-leads-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import CSV
              </Button>
            </Link>
          )}
          <Link to="/leads/new">
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/20" data-testid="add-lead-btn">
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Lead
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search leads..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-leads-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {MILESTONES.map((milestone) => (
                  <SelectItem key={milestone} value={milestone}>
                    {milestone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full md:w-48" data-testid="type-filter">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Residential">Residential</SelectItem>
                <SelectItem value="Commercial">Commercial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {filteredLeads.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                    <TableHead>Customer</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">System Size</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow
                      key={lead.id}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/30"
                      onClick={() => navigate(`/leads/${lead.id}`)}
                      data-testid={`lead-row-${lead.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center">
                            {lead.lead_type === 'Commercial' ? (
                              <Building2 className="w-5 h-5 text-emerald-600" />
                            ) : (
                              <Home className="w-5 h-5 text-emerald-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">
                              {lead.customer_name}
                            </p>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {lead.email}
                            </p>
                          </div>
                          {lead.nonprofit_eligible && (
                            <Badge className="sunraising-badge ml-2">
                              <Heart className="w-3 h-3 mr-1" />
                              SUNRAISING
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {lead.lead_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-slate-600 dark:text-slate-400">
                          {lead.city}, {lead.state}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-medium">
                          {lead.system_size_kw} kW
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-mono font-medium text-emerald-600">
                          ${lead.commission_rep_amount.toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={getMilestoneColor(lead.status)}>
                          {lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/leads/${lead.id}`);
                          }}
                          data-testid={`view-lead-${lead.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Users className="w-12 h-12 mx-auto mb-4 text-slate-300 dark:text-slate-600" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                No leads found
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Get started by adding your first lead'}
              </p>
              {!searchTerm && statusFilter === 'all' && typeFilter === 'all' && (
                <Link to="/leads/new">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Add Your First Lead
                  </Button>
                </Link>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary stats */}
      {filteredLeads.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Leads</p>
              <p className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
                {filteredLeads.length}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total kW</p>
              <p className="text-2xl font-bold font-heading font-mono text-slate-900 dark:text-white">
                {filteredLeads.reduce((sum, l) => sum + l.system_size_kw, 0).toFixed(3)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">Total Commission</p>
              <p className="text-2xl font-bold font-heading font-mono text-emerald-600">
                ${filteredLeads.reduce((sum, l) => sum + l.commission_rep_amount, 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-sm text-slate-500 dark:text-slate-400">SUNRAISING Eligible</p>
              <p className="text-2xl font-bold font-heading text-rose-600">
                {filteredLeads.filter(l => l.nonprofit_eligible).length}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Leads;
